/* eslint-disable @typescript-eslint/no-explicit-any */
import Box from '@mui/material/Box';
import Divider from '@mui/material/Divider';
import Typography from '@mui/material/Typography';
import { useTranslation } from 'react-i18next';

import { DcrType } from '@/core/graphql/generated/types';

import { ReviewArrayField } from '../ReviewArrayField/ReviewArrayField';
import { ReviewRow } from '../ReviewRow/ReviewRow';

import { reviewSectionStyles as sx } from './ReviewSection.styles';

type ReviewSectionProps = {
	sectionName: string;
	fields: readonly string[];
	changedValues: any;
	defaultValues: any;
	currentValues: any;
	fieldTranslations: Record<string, string> | undefined;
	dcrType: DcrType;
};

const NESTED_SECTION_KEYS = [
	'addresses',
	'licenses',
	'parenthcos',
	'hcpSpecialties',
	'degrees',
	'allSpecLocal_VId'
] as const;

export const ReviewSection = ({
	sectionName,
	fields,
	changedValues,
	defaultValues,
	currentValues,
	fieldTranslations,
	dcrType
}: ReviewSectionProps) => {
	const { t } = useTranslation();

	const dirtyFieldsInSection = fields.filter(
		fieldKey => fieldKey in changedValues
	);

	if (dirtyFieldsInSection.length === 0) return null;

	return (
		<Box sx={sx.sectionWrapper}>
			<Box sx={sx.sectionHeader}>
				<Typography sx={sx.sectionTitle}>{t(sectionName)}</Typography>
				{dcrType === DcrType.Change && (
					<Typography sx={sx.columnHeader}>{t('Current')}</Typography>
				)}
				{dcrType === DcrType.Change && (
					<Typography sx={sx.columnHeader}>{t('Requested')}</Typography>
				)}
			</Box>
			<Divider sx={sx.divider} />

			<Box sx={sx.rowsContainer}>
				{dirtyFieldsInSection.map(fieldKey => {
					// Nested Array Fields
					if (NESTED_SECTION_KEYS.includes(fieldKey as any)) {
						const changedItemsMap = changedValues[fieldKey];
						if (!changedItemsMap) return null;

						return (
							<ReviewArrayField
								key={fieldKey}
								fieldKey={fieldKey}
								changedItemsMap={changedItemsMap}
								defaultValues={defaultValues}
								currentValues={currentValues}
								fieldTranslations={fieldTranslations}
								dcrType={dcrType}
							/>
						);
					}

					// Handle Primitive Fields
					const oldVal = defaultValues?.[fieldKey];
					const newVal = currentValues?.[fieldKey];

					return (
						<ReviewRow
							key={fieldKey}
							labelKey={fieldKey}
							oldVal={oldVal}
							newVal={newVal}
							fieldTranslations={fieldTranslations}
							dcrType={dcrType}
						/>
					);
				})}
			</Box>

			<Box mb={4} />
		</Box>
	);
};
